import { defineComponent, reactive, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { Toast, Swipe, SwipeItem } from 'vant';
import { copy } from 'iclipboard';
import QRCode from 'qrcode';
import style from './index.module.scss';
import { useWechat } from '@/hooks/useWechat';
import { useRequest } from '@/hooks/useRequest';
import { useReport } from '@/hooks/useReport';
import { useUpload } from '@/hooks/useUpload';
import { useLongPres } from '@/hooks/useLongPress';
import { fetchGetPosterCopywriting, fetchGetPosterUrl, fetchGetPosterTemplate, generatePosterId, savePoster } from '@/apis/marketing';
import medal from '@/assets/images/marketing/poster/medal.png';
import prevIcon from '@/assets/images/marketing/poster/prev-icon.png';
import nextIcon from '@/assets/images/marketing/poster/next-icon.png';
import shareText from '@/assets/images/marketing/poster/share-text.png';
import { getQueryString } from '@/utils/common';

// 海报数据
interface StateProps {
  qrCode:string;
  posterUrl:string;
  currentIndex:number;
  userId:string;
  templateInfo:PosterAPI.Response.IPosterTemplate | undefined;
}

const default_source = 'WECHAT_BCMXXZX_ACCOUNT'; // 默认来源;
const default_utm_source = 'mgm_poster'; // 默认一级渠道
const default_utm_term = 'poster_mlzhtkd'; // 默认二级渠道

let bucket = '';
let copywriting_id = '';
let file_name = '';
let id = '';
let template_id = 0;

export default defineComponent({
  name: 'Poster',
  setup() {

    const query = getQueryString();
    const state:StateProps = reactive({
      qrCode: '', // 二维码
      posterUrl: '', // 生成的海报地址
      currentIndex: 0, // 当前文案list下标
      userId: '', // 用户ID
      templateInfo: undefined, // 海报模板数据
    });

    // 获取海报模板
    const handleGetPosterTemplate = ():Promise<PosterAPI.Response.IPosterTemplate[]> => new Promise((resolve, reject) => {
      const { run } = useRequest(fetchGetPosterTemplate({ dept: 'orderJump' }), { manual: true });
      run().then((res) => {
        if(res.status !== 200) {
          reject(res);
          return;
        }
        resolve(res.data);
      }).catch((err) => {
        reject(err);
      });
    });

    // 获取二维码地址
    const handleGetQrcode = ():Promise<string> => new Promise((resolve, reject) => {
      const { run: postIdRun } = useRequest(generatePosterId(), { manual: true });
      postIdRun().then((data) => {
        if(data.status !== 200) return;
        id = data.data.id;
        const { run } = useRequest(fetchGetPosterUrl({
          'poster_gexinghua_id': data.data.id,
          'user_id': state.userId,
          'source': query.source || default_source,
          'one_lever_channel_param': query.utm_source || default_utm_source,
          'two_lever_channel_param': query.utm_term || default_utm_term,
          'three_lever_channel_param': query.utm_content,
        }), { manual: true });

        run().then((res) => {
          if(res.status !== 200) {
            reject(res);
            return;
          };
          resolve(res.data);
          // 微信分享初始化
          useWechat({
            title: '我家宝贝加入编程猫啦，逻辑培养选编程！',
            desc: '邀请你一起培养孩子天才大脑～',
            link: res.data,
            imgUrl: 'https://source-static.codemao.cn/bcm_logo.png',
          });
        }).catch((err) => {
          reject(err);
          console.log('获取二维码地址失败', err);
        });
      }).catch((err) => {
        console.log(err);
      });

    });

    // 同时加载模板与二维码
    const handlePoster = () => {
      Promise.all([handleGetPosterTemplate(), handleGetQrcode()]).then(async(res) => {
        await initQRCode(res[1]);
        state.templateInfo = res[0][0];
        template_id = res[0][0].id;
        initPoster(res[0][0].hollow_url);
      }).catch((err) => {
        console.log(err);
      });
    };

    // 初始化二维码
    const initQRCode = (url:string) => {
      QRCode.toDataURL(url, { errorCorrectionLevel: 'L' }).then((url) => {
        console.log(url);
        state.qrCode = url;
      }).catch((err) => {
        console.log('初始化二维码失败', err);
      });
    };

    // 获取用户ID
    const store = useStore();
    const getUserInfo = () => {
      // 获取vuex的用户登录信息
      store.dispatch('GetUserInfo').then((res?:AccountAPI.Response.ILogin) => {
        // 当为undefined时，为未登录
        state.userId = res?.user_info.id ?? '';
        handlePoster();
      }).catch(() => {
        Toast.fail('用户未登录');
      });
    };
    onMounted(() => {
      getUserInfo();
    });

    // 页面初始化埋点
    const { clickReport } = useReport('poster_page');

    // 上传埋点
    const handleSavePosterReport = () => {
      clickReport('save_poster');
      useRequest(savePoster({
        bucket,
        copywriting_id,
        file_name,
        id,
        template_id,
        source: query.source || default_source,
      }));
    };

    // 长按保存图片事件监听 上传埋点
    const longPressSavePoster = () => {
      useLongPres({
        element: document.querySelector('#poster') as HTMLElement,
        func: handleSavePosterReport,
      });
    };

    // 轮播图轮播事件
    const handleSwipeChange = (index:any) => {
      state.currentIndex = index;
      copywriting_id = posterCopyWriting.value?.[index].id;
    };

    // 获取复制文案列表
    const { data: posterCopyWriting } = useRequest(fetchGetPosterCopywriting({ dept: 'orderJump' }));
    watch(() => posterCopyWriting.value, () => {
      copywriting_id = posterCopyWriting.value?.[0].id;
    });
    // 是否出现分享文本list
    const showPosterText = computed(() => posterCopyWriting.value && posterCopyWriting.value.length > 0);

    // 拷贝分享文案
    const handleCopyShareText = () => {
      const shareText = posterCopyWriting.value?.[state.currentIndex].content ?? '';
      if(shareText !== '' && copy(shareText)) {
        // 复制文案埋点
        clickReport('copy_writer');
        Toast.success('复制成功');
        return;
      }
      Toast.fail('复制失败');
    };

    // 初始化海报后完成上传
    const handleUpload = (blob:Blob) => {
      // blob转file
      const posterFiles = new window.File([blob], `poster_${+new Date()}`, { type: 'image/png' });

      const { start } = useUpload({
        accept: 'image/*',
        maxCount: 1,
        isPoster: true,
      });

      start([posterFiles]).then((res) => {
        console.log('海报上传成功');
        bucket = res[0].bucket || '';
        file_name = res[0].fileName;
        console.log(res, bucket, file_name);
      }).catch((err) => {
        console.log('初始化海报上传失败', err);
      });
    };

    // 初始化海报
    const initPoster = (posterBgImageSrc:string) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // 合成海报底图
      const posterBgImage = new Image();
      posterBgImage.crossOrigin = 'anonymous';
      posterBgImage.src = posterBgImageSrc;
      const posterBgImagePromise = new Promise((resolve) => {
        posterBgImage.onload = () => {
          resolve(true);
        };
      });

      // 合成二维码
      const qrCodeImage = new Image();
      qrCodeImage.src = state.qrCode;
      const qrCodeImagePromise = new Promise((resolve) => {
        qrCodeImage.onload = () => {
          resolve(true);
        };
      });

      // 两张图片都加载完成后绘制于Canvas中
      const drawAllImg = Promise.all([posterBgImagePromise, qrCodeImagePromise]).then(() => {
        if(!ctx) {
          throw new Error('未找到画布上下文');
        }
        if(!state.templateInfo) {
          throw new Error('未获取到模板信息');
        }
        const width = +state.templateInfo.width;
        const height = +state.templateInfo.height;
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(posterBgImage, 0, 0, width, height);
        ctx.drawImage(qrCodeImage, +state.templateInfo.position_x, +state.templateInfo.position_y, +state.templateInfo.qrcode_width, +state.templateInfo.qrcode_height);
        ctx.font = '24px 宋体';
        ctx.fillStyle = '#fff';
        ctx.direction = 'rtl';
        ctx.fillText(state.userId, width - 10, 40);
      });

      drawAllImg.then(() => {
        if(!ctx) {
          throw new Error('未找到画布上下文');
        }
        ctx.canvas.toBlob((blob) => {
          const posterUrl = URL.createObjectURL(blob);
          state.posterUrl = posterUrl;
          longPressSavePoster();
          handleUpload(blob as Blob);
        });
      }).catch((err) => {
        console.log('生成海报失败', err);
      });
    };

    return () => (
      <div class={style.container}>
        <img class={style.medal} src={medal} alt="勋章" />
        <p class={style.title}>恭喜宝贝获得未来创造者勋章</p>
        <main class={style.main}>
          <img id="poster" src={state.posterUrl} />
        </main>
        <footer class={style.footer}>
          <div class={style.footer_group}>
            <img class={style.prevIcon} src={prevIcon} alt="prev" />
            <div class={style.footer_group_box}>
              <Swipe class={style.swipe_box} show-indicators={false} onChange={handleSwipeChange}>
                {showPosterText.value && posterCopyWriting.value?.map((item) => (
                  <SwipeItem class={style.box_text}>{item.content}</SwipeItem>
                ))}
              </Swipe>
            </div>
            <img class={style.nextIcon} src={nextIcon} alt="next" />
          </div>
          <p class={style.footer_tips}>左右滑动可切换文案</p>
          <p class={style.footer_desc}>长按保存海报分享 给宝贝打卡点亮勋章吧!</p>
          <div class={style.footer_btn} onClick={handleCopyShareText}>
            <img src={shareText} alt="分享文案" />
          </div>
        </footer>
      </div>
    );
  },
});
